import { AxiosError } from 'axios';
import { ReactNode, useState } from 'react';
import { useMutation } from 'react-query';

import { LinkReservationToProfileBodyDto } from '@numa-group/reservation-v3-api';

import { RichContent } from '@src/components/common/formatting/rich-content';
import { Modal } from '@src/components/molecules/modal';
import { deepLinks } from '@src/constants/deep-links';
import { useCmsContent } from '@src/contexts/cms-content-context';
import { useToast } from '@src/contexts/toast-context';
import { RequestLinkReservationForm } from '@src/features/trip-linking/components/request-link-reservation-form';
import { useReservationLinkingAnalytics } from '@src/features/trip-linking/hooks/use-trip-linking-analytics';
import { reservationV3Api } from '@src/services';

import RequestTripLinkingSuccessModal from '../request-link-reservation-success-modal/request-link-reservation-success-modal';

import styles from './request-link-reservation-modal.module.css';

type RequestLinkReservationErrorCode =
  | 'INVALID_JWT_TOKEN'
  | 'INVALID_AUTH_TYPE'
  | 'RESERVATION_NOT_FOUND'
  | 'GUEST_NOT_FOUND'
  | 'EMAIL_SENDING_FAILED'
  | 'RESERVATION_ALREADY_LINKED_TO_PROFILE';

type RequestLinkReservationResponseErrorDto = {
  errorCode: RequestLinkReservationErrorCode;
};

type RequestLinkReservationModalProps = {
  children: (open: () => void, isOpen: boolean) => ReactNode;
};

const RequestLinkReservationModal = ({ children }: RequestLinkReservationModalProps) => {
  const content = useCmsContent();
  const { addToast } = useToast();
  const [isTripLinkingModalOpen, setIsTripLinkingModalOpen] = useState(false);
  const [isTripLinkingSuccessModalOpen, setIsTripLinkingSuccessModalOpen] = useState(false);
  const analytics = useReservationLinkingAnalytics();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const {
    linkReservationToProfileTwoStepFeature: {
      errorEmailSendingFailed,
      errorReservationNotFound,
      errorGuestNotFound,
      errorReservationAlreadyLinkedToProfile,
      errorUnauthorised,
      requestTitle,
      requestDescription,
    },
  } = content;

  const errorMap: Record<RequestLinkReservationErrorCode, string> = {
    RESERVATION_NOT_FOUND: errorReservationNotFound,
    EMAIL_SENDING_FAILED: errorEmailSendingFailed,
    GUEST_NOT_FOUND: errorGuestNotFound,
    INVALID_AUTH_TYPE: errorUnauthorised,
    INVALID_JWT_TOKEN: errorUnauthorised,
    RESERVATION_ALREADY_LINKED_TO_PROFILE: errorReservationAlreadyLinkedToProfile,
  };

  const { mutateAsync: linkReservation, isLoading } = useMutation<
    LinkReservationToProfileBodyDto,
    AxiosError<RequestLinkReservationResponseErrorDto>,
    LinkReservationToProfileBodyDto
  >(
    ['-reservation-linking'],
    async (data: LinkReservationToProfileBodyDto) => {
      setErrorMessage(undefined);
      await reservationV3Api.linkReservationToProfile(data);
      return data;
    },
    {
      onSuccess: (data) => {
        setIsTripLinkingModalOpen(false);
        setIsTripLinkingSuccessModalOpen(true);
        analytics.trackRequestSuccess({ data });
      },
      onError: async (error, variables) => {
        const errorData = error.response?.data;
        const errorCode = errorData?.errorCode;
        const errorMessage = (errorCode && errorMap[errorCode]) ?? content.generalErrorText;
        addToast({ type: 'error', content: errorMessage, title: content.error });
        analytics.trackRequestFailure(errorCode ?? 'server-error', variables);
      },
    },
  );

  const onOpenChange = (open: boolean) => {
    setIsTripLinkingModalOpen(open);
    setErrorMessage(undefined);
  };

  const openModal = () => setIsTripLinkingModalOpen(true);

  return (
    <>
      <Modal.Container deepLink={deepLinks.linkTrip} onOpenChange={onOpenChange} open={isTripLinkingModalOpen}>
        <Modal title={requestTitle}>
          <div className={styles.modalHeader}>
            <RichContent html={requestDescription} />
          </div>

          <RequestLinkReservationForm isLoading={isLoading} onSubmit={linkReservation} errorMessage={errorMessage} />
        </Modal>

        {children(openModal, isTripLinkingModalOpen)}
      </Modal.Container>

      <RequestTripLinkingSuccessModal
        isOpen={isTripLinkingSuccessModalOpen}
        setIsOpen={() => setIsTripLinkingSuccessModalOpen(false)}
      />
    </>
  );
};

export default RequestLinkReservationModal;
